import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  Cancel,
  CancelTokenSource,
  ResponseType as AxiosResponseType
} from "axios";

/**
 * Ajax configuration for the init (initAjaxStore)
 */
export interface IAjaxServerConfiguration {
  apiUrl?: string;
  interceptor?: (config: AxiosRequestConfig) => Promise<AxiosRequestConfig>;
}
/**
 * The error object
 */
export interface IApiError {
  message?: string;
  code?: number;
}
/**
 * The object returned by every call
 */
export interface ICancellableResult<R> {
  cancelled: boolean;
  code: number;
  datas: IResponseWrapper<R> | null;
}

interface IResponseWrapper<R> {
  response: R;
  hasError: boolean;
  errors: Record<string, string>;
}

interface IParameteredAjaxCallType {
  post: string;
  put: string;
  patch: string;
}
interface IAjaxCallType {
  get: string;
  delete: string;
}

/**
 * Object parameter for the call
 * Contains query param, url param, payload, etc.
 */
export interface IBaseAjaxCallParam<P> {
  /**
   * Body object.
   */
  payload?: P | null;
  /**
   * Headers you need to add for this specific call.
   */
  headers?: Record<string, string | number | boolean> | null;
  /**
   * @summary If you need the call to be cancelled
   * @example
   *    multiple search call and only last result must be handled
   *    use cancelled so all other will return with Cancelled = true
   *    in ICancellableResult.
   */
  canBeCancelled?: boolean;
  /**
   * Url use for cancelling purpose, if null, will use the default url in call.
   * You can use any kind of unique string for cancelling.
   */
  cancelUrl?: string | null;
  /**
   * Set the response type, see AxiosResponseType
   */
  responseType?: AxiosResponseType;
  /**
   * @summary Used to add some parameter to the url as query parameter
   * @example
   *    With url : /users
   *    Goal : /users?search='a'
   *    You just need to add in the object { search: 'a' }
   *    The system will automatically add all the key inserted in the url as query parameter
   *    and their value as value of it.
   */
  queryParameter?: Record<string, string> | null;
  /**
   * @summary Used to add some parameter to the url as url path
   * @example
   *    With url : /users
   *    Goal : /users/xxxx@yyy.vvv/test
   *    You just need to add every data as string value in the array
   *    Here : ['xxxx@yyy.vvv','test']
   *    The system will automatically add all the value in the url as path url
   */
  urlParameter?: string[] | null;
  /**
   * @summary Name for file to be downloaded
   * @example
   *    Name for the file to be downloaded
   *    Download activate automatically if set
   */
  fileDownloadName?: string | null;
}

class AjaxCall {
  private cancellableObject: { [key: string]: CancelTokenSource } = {};

  /**
   * Helper to know if everything was ok during call
   * @param call Result of a call
   * @returns boolean if true, everything was ok, else check on cancelled & error
   */
  public isCallValidAndNotCancelled<T>(call: ICancellableResult<T> | null): boolean {
    return (
      call != null &&
      call.cancelled === false &&
      call.code >= 200 &&
      call.code < 300 &&
      call.datas != null &&
      call.datas.hasError === false
    );
  }

  private cancelGenericPreviousRequest(url: string): void {
    if (url != null) {
      const tokenHandler = this.cancellableObject[url];
      if (tokenHandler != null) {
        tokenHandler.cancel("Cancelled");
      }
    }
  }

  private addNewGenericCancellableToken(url: string, tokenSource: CancelTokenSource): void {
    this.cancellableObject[url] = tokenSource;
  }

  private createConfig(
    source: CancelTokenSource | undefined,
    headers: Record<string, string | number | boolean> | null | undefined,
    responseType?: AxiosResponseType
  ): AxiosRequestConfig {
    const h: AxiosRequestHeaders = headers as AxiosRequestHeaders;
    const config: AxiosRequestConfig = {
      cancelToken: source != null ? source.token : undefined,
      headers: h,
      responseType
    };
    return config;
  }

  private async callWithParameter<P = null>(
    url: string,
    source: CancelTokenSource | undefined,
    type: keyof IParameteredAjaxCallType,
    headers: Record<string, string | number | boolean> | null | undefined,
    parameter: P | null = null,
    responseType?: AxiosResponseType
  ): Promise<AxiosResponse<unknown>> {
    const config = this.createConfig(source, headers, responseType);
    return axios[type](url, parameter, config);
  }

  private async callWithoutParameter(
    url: string,
    source: CancelTokenSource | undefined,
    type: keyof IAjaxCallType,
    headers: Record<string, string | number | boolean> | null | undefined,
    responseType?: AxiosResponseType
  ): Promise<AxiosResponse<unknown>> {
    const config = this.createConfig(source, headers, responseType);
    return axios[type](url, config);
  }

  private replaceAll(val: string, needReplace = "//", replaceBy = "/"): string {
    while (val.indexOf(needReplace) > -1) {
      val = val.replace(needReplace, replaceBy);
    }
    return val;
  }

  private downloadFile(documentName: string, documentContent: any) {
    const type = "application/octet-stream";
    const blob = new Blob([documentContent], { type: type });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = documentName;
    link.click();
    link.remove();
  }

  private enrichAndCleanUrl(
    url: string,
    queryParams?: Record<string, string> | null,
    urlParam?: string[] | null
  ): string {
    let newUri = url;
    const qParamString = queryParams ? "?" + new URLSearchParams(queryParams).toString() : "";
    if (urlParam && urlParam.length > 0) {
      const uParamString = urlParam.join("/");
      newUri = `${newUri}/${uParamString}`;
    }
    newUri = `${newUri}${qParamString}`;
    if (newUri.indexOf("https://") === 0) {
      newUri = "https://" + this.replaceAll(newUri.replace("https://", ""));
    } else if (newUri.indexOf("http://") === 0) {
      newUri = "http://" + this.replaceAll(newUri.replace("http://", ""));
    } else {
      newUri = this.replaceAll(newUri);
    }
    return newUri;
  }

  private mustBeCancelled(cancelUrl: string | null, url: string) {
    this.cancelGenericPreviousRequest(cancelUrl ?? url);
    const CancelToken = axios.CancelToken;
    const source: CancelTokenSource | undefined = CancelToken.source();
    this.addNewGenericCancellableToken(cancelUrl ?? url, source);
  }

  public async baseAjaxCall<R, P = null>(
    type: keyof IAjaxCallType | keyof IParameteredAjaxCallType = "post",
    url: string,
    params: IBaseAjaxCallParam<P> = {
      headers: null,
      payload: null,
      canBeCancelled: false
    }
  ): Promise<ICancellableResult<R>> {
    let source: CancelTokenSource | undefined;
    if (params.canBeCancelled) {
      this.mustBeCancelled(params.cancelUrl as string | null, url);
    }
    if (params.fileDownloadName && params.fileDownloadName.length > 0) {
      params.responseType = "blob";
    }
    const returnValue = {
      cancelled: false,
      datas: null
    } as ICancellableResult<R>;
    try {
      let response: AxiosResponse | null = null;
      url = this.enrichAndCleanUrl(url, params.queryParameter, params.urlParameter);
      switch (type) {
        case "get":
        case "delete":
          response = await this.callWithoutParameter(url, source, type, params.headers, params.responseType);
          break;
        case "post":
        case "put":
        case "patch":
          response = await this.callWithParameter<P>(
            url,
            source,
            type,
            params.headers,
            params.payload,
            params.responseType
          );
          break;
      }
      if (response && response.data != null) {
        if (params.fileDownloadName && params.fileDownloadName.length > 0) {
          this.downloadFile(params.fileDownloadName, response.data);
        } else {
          returnValue.datas = response.data;
        }
      }
      returnValue.code = response.status;
    } catch (error) {
      const err: AxiosError & Cancel = error as AxiosError & Cancel;
      if (err.message === "Cancelled") {
        returnValue.cancelled = true;
      } else if (err.response && err.response.data) {
        returnValue.code = err.response.status;
        returnValue.datas = err.response.data;
      }
    }
    return returnValue;
  }

  /**
   * Basic get
   * @template R Type of returning object
   * @param url base Url to call
   * @param params Parameters for the call
   * @returns Promise of ICancellableResult
   */
  public get<R>(url: string, params?: IBaseAjaxCallParam<null>): Promise<ICancellableResult<R>> {
    return this.baseAjaxCall<R>("get", url, params);
  }

  /**
   * Basic post
   * @template R Type of returning object
   * @template P Type of object payload
   * @param url base Url to call
   * @param params Parameters for the call
   * @returns Promise of ICancellableResult
   */
  public post<R, P = null>(url: string, params?: IBaseAjaxCallParam<P>): Promise<ICancellableResult<R>> {
    return this.baseAjaxCall<R, P>("post", url, params);
  }

  /**
   * Basic patch
   * @template R Type of returning object
   * @template P Type of object payload
   * @param url base Url to call
   * @param params Parameters for the call
   * @returns Promise of ICancellableResult
   */
  public patch<R, P = null>(url: string, params?: IBaseAjaxCallParam<P>): Promise<ICancellableResult<R>> {
    return this.baseAjaxCall<R, P>("patch", url, params);
  }

  /**
   * Basic delete
   * @template R Type of returning object
   * @param url base Url to call
   * @param params Parameters for the call
   * @returns Promise of ICancellableResult
   */
  public delete<R>(url: string, params?: IBaseAjaxCallParam<null>): Promise<ICancellableResult<R>> {
    return this.baseAjaxCall<R>("delete", url, params);
  }

  /**
   * Use to initialize the system adding base url and interceptor
   * @param ajaxConfig Configuration for ajaxCall
   */
  public async initAjaxStore(ajaxConfig: IAjaxServerConfiguration = {}) {
    if (ajaxConfig.apiUrl) {
      axios.defaults.baseURL = ajaxConfig.apiUrl;
    }
    if (ajaxConfig.interceptor) {
      axios.interceptors.request.use(ajaxConfig.interceptor);
    }
  }
}

export const ajaxCall = new AjaxCall();
